import React from "react";
import { Row, Col } from "antd";
import { BookyDynamicInput } from "../Forms/BookyDynamicInput";
import { BookyPhoneInput } from "../Forms/BookyPhoneInput";
import { BookyCheckboxList } from "../Forms/BookyCheckboxList";
import { BookyDynamicCheckbox } from "../Forms/BookyDynamicCheckbox";
import { BookyCheckbox } from "../Forms/BookyCheckbox";
import { MemberFieldTypes } from "../../Types/MemberFieldTypes";
import { OneFrontDatePicker } from "./BookyDatePicker";
import { BookySelectDropDown } from "./BookySelectDropDown";
import { WebApiModelsMemberField, WebApiModelsMembershipProduct } from "../../Repository/eventbookyapi";
import { MembershipProductType } from "../../Types/MembershipProductType";
import { MemberStatusType } from "../../Types/MemberStatusType";
import { countryOptions } from "../../Helpers/CountryOptions";
import { getCurrentLanguage } from "../../Helpers/RouteHelper";

const isPairRow = (field: any, nextField: any) => {
    if (!field || !nextField) return false;
    const pairs = [
        [MemberFieldTypes.FirstName, MemberFieldTypes.LastName],
        [MemberFieldTypes.LastName, MemberFieldTypes.FirstName],
        [MemberFieldTypes.PostalArea, MemberFieldTypes.PostalCode],
        [MemberFieldTypes.PostalCode, MemberFieldTypes.PostalArea],
        [MemberFieldTypes.Email, MemberFieldTypes.Email2],
    ];
    return pairs.some(
        ([a, b]) => field.columnType === a && nextField.columnType === b
    );
};


// Props: memberFieldSettings, GetText, groups, user, onPhoneChange, onPhoneChange2
export const OutPutMemberEditFields = ({
    memberFieldSettings,
    membershipProducts,
    GetText,
    groups,
    user,
    onPhoneChange,
    onPhoneChange2,
    isAdmin,
    hasPingPaymentIntegration
}: {
    memberFieldSettings: WebApiModelsMemberField[],
    membershipProducts: WebApiModelsMembershipProduct[],
    GetText: (key: string) => string,
    groups: any[],
    user: any,
    onPhoneChange: (value: any) => void,
    onPhoneChange2?: (value: any) => void,
    isAdmin: boolean,
    hasPingPaymentIntegration?: boolean
}) => {
    const memberStatusOptions = [
        { value: MemberStatusType.Active, label: GetText("MemberList_StatusActive") },
        { value: MemberStatusType.Cancelled, label: GetText("MemberList_StatusCancelled") },
        { value: MemberStatusType.UnconfirmedInitialPayment, label: GetText("MemberList_StatusUnconfirmedInitialPayment") },
    ];

    const fields = [...memberFieldSettings]
        .filter(f => f.uiControl !== 4) // Exclude lookup fields (uiControl 4)
        .sort((a, b) => (a.columnOrder ?? 0) - (b.columnOrder ?? 0));
    const result: JSX.Element[] = [];
    let i = 0;

    while (i < fields.length) {
        const field = fields[i];
        const nextField = fields[i + 1];

        // Default spans
        let firstSpan = 12;
        let secondSpan = 12;

        // Special case: postalcode should be smaller (span 8), the other 16
        if (
            (field.columnType === MemberFieldTypes.PostalArea && nextField.columnType === MemberFieldTypes.PostalCode) ||
            (field.columnType === MemberFieldTypes.PostalCode && nextField.columnType === MemberFieldTypes.PostalArea)
        ) {
            firstSpan = field.columnType === MemberFieldTypes.PostalCode ? 8 : 16;
            secondSpan = nextField.columnType === MemberFieldTypes.PostalCode ? 8 : 16;
        }
        if (isPairRow(field, nextField)) {

            result.push(
                <Row gutter={16} key={`${field.id}-${nextField.id}`}>
                    <Col span={firstSpan}>
                        <BookyDynamicInput
                            key={field.id}
                            fieldType={field.columnType}
                            fieldId={field.id}
                            isAdmin={isAdmin}
                            memberFieldSettings={memberFieldSettings}
                            validateEmailIfFilledIn={
                                field.columnType === MemberFieldTypes.Email ||
                                field.columnType === MemberFieldTypes.Email2
                            }
                        />
                    </Col>
                    <Col span={secondSpan}>
                        <BookyDynamicInput
                            key={nextField.id}
                            fieldType={nextField.columnType}
                            fieldId={nextField.id}
                            isAdmin={isAdmin}
                            memberFieldSettings={memberFieldSettings}
                            validateEmailIfFilledIn={
                                nextField.columnType === MemberFieldTypes.Email ||
                                nextField.columnType === MemberFieldTypes.Email2
                            }
                        />
                    </Col>
                </Row>
            );
            i += 2;
            continue;
        }

        // Render all other fields as before
        if (field.columnType == MemberFieldTypes.Custom) {
            result.push(
                <BookyDynamicInput
                    key={field.id}
                    fieldId={field.id}
                    fieldType={field.columnType}
                    isAdmin={isAdmin}
                    memberFieldSettings={memberFieldSettings}
                    validateEmailIfFilledIn={false}
                />
            );
        }
        else if (field.columnType === MemberFieldTypes.PhoneNumber || field.columnType === MemberFieldTypes.PhoneNumber2) {
            if (field.visibleInAdmin && isAdmin || field.visiblePublic && !isAdmin) {
                result.push(
                    <BookyPhoneInput
                        key={field.id}
                        data-testid={field.columnType === MemberFieldTypes.PhoneNumber2 ? "phone-number-2-input" : "phone-number-input"}
                        label={GetText("EditMember_PhoneNumber") + (field.columnType === MemberFieldTypes.PhoneNumber2 ? " 2" : "")}
                        disabled={user.hasOnlyReadRights}
                        name={field.columnType === MemberFieldTypes.PhoneNumber2 ? "phoneNumber2" : "phoneNumber"}
                        onchange={field.columnType === MemberFieldTypes.PhoneNumber2 ? onPhoneChange2 : onPhoneChange}
                        req
                        placeholder={""}
                    />
                );
            }
        }
        else if (field.columnType === MemberFieldTypes.JoinedDate) {
            if (field.visibleInAdmin && isAdmin || field.visiblePublic && !isAdmin) {
                result.push(
                    <OneFrontDatePicker
                        key={field.id}
                        label={GetText("EditMember_JoinedDate")}
                        name="joinedDate"
                        placeholder={GetText("EditMember_JoinedDatePlaceHolder")}
                    />
                );
            }
        }
        else if (field.columnType === MemberFieldTypes.Country) {
            if (field.visibleInAdmin && isAdmin || field.visiblePublic && !isAdmin) {
                const lang = getCurrentLanguage();
                result.push(
                    <BookySelectDropDown
                        key={field.id}
                        allowClear={true}
                        showSearch={true}
                        label={GetText("EditMember_Country")}
                        name="country"
                        req={isAdmin ? field.mandatoryAdmin : field.mandatoryPublic}
                        options={countryOptions.map((c) => ({
                            value: c.code,
                            label: lang === "se" ? c.se : c.en
                        }))}
                    />
                );
            }
        }
        else if (field.columnType == MemberFieldTypes.Membership) {
            // One field for both membership types (dropdown) and add-ons (checkboxes, shown only if any exist).
            const membershipTypes = membershipProducts.filter(mp => mp.membershipProductType == MembershipProductType.MembershipType);
            const addons = membershipProducts.filter(mp => mp.membershipProductType == MembershipProductType.Addon);
            if (field.visibleInAdmin && isAdmin || field.visiblePublic && !isAdmin && membershipProducts.length > 0) {
                if (isAdmin || membershipTypes.length > 0) {
                    result.push(
                        <BookySelectDropDown
                            key={field.id}
                            allowClear={true}
                            handleChange={() => { }}
                            label={GetText("EditMember_Membership")}
                            name="membership"
                            req={isAdmin ? field.mandatoryAdmin : field.mandatoryPublic}
                            options={membershipTypes.map(mp => ({ label: mp.name + " (" + mp.price + " SEK)", value: mp.id }))}
                        />
                    );
                }
                if (addons.length > 0) {
                    result.push(
                        <BookyCheckboxList
                            key={field.id + "-addon"}
                            name="membershipAddon"
                            options={addons.map(mp => ({ label: mp.name + " (" + mp.price + " SEK)", value: mp.id }))}
                        />
                    );
                }
            }
        }
        else if (field.columnType == MemberFieldTypes.MembershipAddon) {
            // Obsolete: add-ons are rendered by the Membership field above.
        }
        else if (field.columnType === MemberFieldTypes.EmailNotificationPreference) {
            if (field.visibleInAdmin && isAdmin || field.visiblePublic && !isAdmin) {
                result.push(
                    <BookyDynamicCheckbox
                        key={field.id}
                        fieldId={field.id}
                        fieldType={MemberFieldTypes.EmailNotificationPreference}
                        isAdmin={isAdmin}
                        memberFieldSettings={memberFieldSettings}
                    />
                );
            }
        } else {
            result.push(
                <BookyDynamicInput
                    key={field.id}
                    fieldId={field.id}
                    fieldType={field.columnType}
                    isAdmin={isAdmin}
                    memberFieldSettings={memberFieldSettings}
                    validateEmailIfFilledIn={
                        field.columnType === MemberFieldTypes.Email ||
                        field.columnType === MemberFieldTypes.Email2
                    }
                />
            );
        }
        i++;
    }

    if (isAdmin) {
        result.push(
            <BookyCheckboxList
                key={"groups"}
                label={GetText("EditMember_Groups")}
                bordered={true}
                options={groups}
                name="groups"
                placeholder={GetText("EditMember_Groups")}
            />
        );

        result.push(
            hasPingPaymentIntegration ?
                <BookySelectDropDown
                    key={"status"}
                    allowClear={false}
                    label={GetText("EditMember_Status")}
                    name="status"
                    options={memberStatusOptions}
                />
                :
                <BookyCheckbox
                    key={"status"}
                    alignLabelRight={true}
                    label={"Avslutad"}
                    name="status"
                />
        );
    }


    return <>{result}</>;
};